import { useState, useEffect, useCallback } from 'react';
import { 
  Check, 
  ChevronRight,
  Loader2,
  CheckCircle2,
  ArrowRight,
  GraduationCap,
  Plane,
  Users,
  Palmtree,
  Share2,
  FileCheck,
  Globe,
  DollarSign,
  TrendingUp,
  Award,
  Shield,
  Clock,
  Star,
  Gift,
  Sparkles,
  Video,
  Calendar,
  BookOpen,
  Download,
  Zap,
  Bot,
  Mail,
  BarChart3,
  Megaphone,
  Target,
  CalendarCheck,
  Repeat,
  Crown,
  Building2,
  Briefcase,
  ArrowDown,
  UserPlus,
  Network,
  Wallet,
  Layers,
  AlertTriangle,
  Lock,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast, Toaster } from 'sonner';
import { ClubViajemosLogoFull } from './ClubViajemosLogo';
import { API_URL } from '../config/api';
import { downloadFile } from '../utils/downloadFile';

import PricingOption from './vip/PricingOption';
import TravelEmpireFactory from './vip/TravelEmpireFactory';
import PaymentPartners from './vip/PaymentPartners';
import NetworkBuilderSection from './vip/NetworkBuilderSection';


// Countries list
const COUNTRIES = [
  "Estados Unidos", "México", "Colombia", "Argentina", "España", 
  "Perú", "Chile", "Ecuador", "Venezuela", "Guatemala", 
  "Cuba", "República Dominicana", "Honduras", "El Salvador", 
  "Nicaragua", "Costa Rica", "Panamá", "Puerto Rico", "Uruguay", 
  "Paraguay", "Bolivia", "Otro"
];

// ── Configuración editable de precios / bonos / términos de la promoción ──
const PROMO_CONFIG = {
  starter: {
    paymentPlanText: "o $197 hoy + 6 pagos de $97 (total $779)",
    bono: { text: "¡Paga completo en cuotas con Klarna, Afterpay, Hotmart y/o Mercado Pago y llévate 10 subagencias GRATIS (valor $1,970) para empezar a facturar desde el primer día!" },
  },
  pro: {
    paymentPlanText: "o $397 hoy + 8 pagos de $159 (total $1,669)",
    bono: { text: "¡Paga completo en cuotas con Klarna, Afterpay, Hotmart y/o Mercado Pago y llévate 40 subagencias GRATIS (valor $7,880) y acelera tu red desde ya!" },
  },
  empire: {
    paymentPlanText: "o $1,995 hoy + 6 pagos de $500 (total $4,995)",
    bono: {
      special: true,
      strike: 4995,
      price: 3495,
      savings: 1500,
      text: "¡Paga completo en cuotas con Klarna, Afterpay, Hotmart y/o Mercado Pago y quédate con el 100% de tus comisiones de agencia durante 6 meses! Solo 10 cupos por webinar.",
    },
  },
};


// Detailed Secretos Information
// Secreto Card Component
// Main VIP Sales Page
export const VIPAccessPage = ({ promoMode = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [audience, setAudience] = useState('agencias');
  // Promo gate removido: la página se muestra directamente
  const [gateOk] = useState(true);
  const [promoDate, setPromoDate] = useState('');
  const [promoCountdown, setPromoCountdown] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [paymentType, setPaymentType] = useState('full');
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(0);
  const [validatingCode, setValidatingCode] = useState(false);

  // $500 Code Form state
  const [codigoForm, setCodigoForm] = useState({ nombre: '', email: '', telefono: '', pais: '' });
  const [codigoResult, setCodigoResult] = useState(null);
  const [codigoError, setCodigoError] = useState('');
  const [codigoLoading, setCodigoLoading] = useState(false);

  // Promo: gate + fecha de hoy + cuenta regresiva a medianoche (solo /promo)
  useEffect(() => {
    if (!promoMode) return;
    const fmt = () => {
      const now = new Date();
      setPromoDate(now.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }));
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      let diff = Math.max(0, Math.floor((end - now) / 1000));
      const h = String(Math.floor(diff / 3600)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
      const s = String(diff % 60).padStart(2, '0');
      setPromoCountdown(`${h}:${m}:${s}`);
    };
    fmt();
    const id = setInterval(fmt, 1000);
    return () => clearInterval(id);
  }, [promoMode]);

  const submitGate = () => {};

  const handleCodigoSubmit = async (e) => {
    e.preventDefault();
    setCodigoError('');
    setCodigoLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/certificados/generar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(codigoForm)
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setCodigoResult(data.codigo);
      } else {
        setCodigoError(data.detail || 'Error al generar el codigo');
      }
    } catch {
      setCodigoError('Error de conexion. Intenta de nuevo.');
    } finally {
      setCodigoLoading(false);
    }
  };
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    pais: '',
    telefono: '',
    fue_referido: null,
    nombre_agente_referidor: ''
  });

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 10) {
      setPaymentStatus('timeout');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Pago exitoso! Bienvenido a la familia.');
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Poll payment status error:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    }
  }, []);

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const status = params.get('status');
    
    if (sessionId && status === 'success') {
      setPaymentStatus('checking');
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Pago cancelado. Puedes intentar de nuevo.');
      // Clean URL params so user can retry
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [pollPaymentStatus]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setDiscountCode('');
    setDiscountApplied(0);
    setPaymentStatus(null);
    setFormData({
      nombre: '', apellido: '', email: '', pais: '', telefono: '',
      fue_referido: null, nombre_agente_referidor: ''
    });
    setShowModal(true);
  };

  const handleValidateCode = async () => {
    if (!discountCode.trim()) return;
    setValidatingCode(true);
    try {
      const response = await fetch(`${API_URL}/api/codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        setDiscountApplied(data.discount_amount);
        toast.success(`Código aplicado: -$${data.discount_amount} USD de descuento`);
      } else {
        const err = await response.json();
        setDiscountApplied(0);
        toast.error(err.detail || 'Código inválido');
      }
    } catch {
      toast.error('Error al validar código');
    }
    setValidatingCode(false);
  };

  const handleSubmit = async () => {
    if (!formData.nombre || !formData.apellido || !formData.email || !formData.pais) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    if (formData.fue_referido === null) {
      toast.error('Por favor indica si fuiste referido');
      return;
    }
    
    if (formData.fue_referido && !formData.nombre_agente_referidor) {
      toast.error('Por favor ingresa el nombre del agente que te refirió');
      return;
    }

    setLoading(true);
    
    try {
      const leadResponse = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          plan_seleccionado: selectedPlan
        })
      });
      
      if (!leadResponse.ok) throw new Error('Error al crear registro');
      
      const leadData = await leadResponse.json();
      
      // Retry logic for Stripe checkout (5 attempts with exponential backoff)
      let checkoutData = null;
      let lastError = null;
      const maxAttempts = 5;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          if (attempt > 0) {
            toast.info(`Reintentando conexion... (intento ${attempt + 1}/${maxAttempts})`);
          }
          const checkoutResponse = await fetch(`${API_URL}/api/checkout/create`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              lead_id: leadData.id,
              origin_url: window.location.origin,
              payment_type: paymentType,
              discount_code: discountApplied > 0 ? discountCode.trim() : null
            })
          });
          
          if (checkoutResponse.ok) {
            checkoutData = await checkoutResponse.json();
            break;
          } else {
            const errData = await checkoutResponse.json().catch(() => ({}));
            lastError = errData.detail || 'Error al crear sesion de pago';
            if (checkoutResponse.status === 400) break; // Don't retry client errors
          }
        } catch {
          lastError = 'Error de conexion con el servidor';
        }
        if (attempt < maxAttempts - 1) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
      }
      
      if (checkoutData?.checkout_url) {
        window.location.href = checkoutData.checkout_url;
      } else {
        toast.error(lastError || 'No se pudo conectar con el procesador de pagos. Intenta de nuevo.');
        setLoading(false);
      }
      
    } catch (error) {
      toast.error('Error al procesar. Intenta de nuevo.');
      setLoading(false);
    }
  };

  // Payment Success Screen
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0a0f1a] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-8 max-w-lg w-full text-center shadow-xl">
          <div className="w-20 h-20 bg-[#80C753]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-[#80C753]" />
          </div>
          <h1 className="text-3xl font-bold text-[#1D1D1B] dark:text-white mb-4">
            ¡Bienvenido a la Familia!
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Tu pago fue procesado exitosamente. Recibirás un email con los próximos pasos y acceso a tu cuenta.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="bg-[#80C753] hover:bg-[#6ab043] text-white"
          >
            Ir al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0f1a]">
      {/* Promo bar — SOLO HOY (solo /promo) */}
      {promoMode && (
        <div className="bg-gradient-to-r from-[#E23744] via-[#ff5a67] to-[#E23744] text-white" data-testid="promo-bar">
          <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-center">
            <span className="inline-flex items-center gap-1.5 text-sm font-black uppercase tracking-wide">
              <Sparkles className="h-4 w-4" /> SOLO HOY
            </span>
            <span className="text-xs sm:text-sm font-semibold capitalize opacity-95">{promoDate}</span>
            <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold bg-black/20 rounded-full px-3 py-1">
              Termina en <span className="font-mono tabular-nums" data-testid="promo-countdown">{promoCountdown}</span>
            </span>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white dark:bg-[#151B2B] border-b border-slate-200 dark:border-slate-700 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <ClubViajemosLogoFull showSubtitle={false} />
          <div className="hidden sm:flex items-center gap-2 text-sm text-slate-500">
            <Shield className="h-4 w-4 text-[#80C753]" />
            <span>Garantía de 7 días</span>
          </div>
        </div>
      </header>

      {/* Pricing Section — Inmersiva con destinos del mundo */}
      <section className="relative py-20 md:py-28 overflow-hidden" id="pricing">
        {/* Fondo: destinos turísticos del mundo (sensación de libertad) */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1504681869696-d977211a5f4c?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            alt="Destinos turísticos del mundo"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/85 via-[#0B0F19]/72 to-[#0B0F19]/90" />
        </div>
        {/* Orbes animados de fondo */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 -left-24 w-[28rem] h-[28rem] bg-[#80C753]/18 rounded-full blur-[130px] cv-animate-blob" />
          <div className="absolute top-1/3 -right-24 w-[28rem] h-[28rem] bg-[#45A0EE]/18 rounded-full blur-[130px] cv-animate-blob cv-delay-200" />
          <div className="absolute bottom-0 left-1/3 w-[26rem] h-[26rem] bg-[#D4AF37]/12 rounded-full blur-[140px] cv-animate-blob cv-delay-400" />
        </div>
        {/* Textura de grilla */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '46px 46px' }} />

        <div className="relative max-w-6xl mx-auto px-4">
          <div className="text-center mb-12 cv-fade-in-up">
            <div className="inline-flex items-center gap-2 cv-glass rounded-full px-4 py-2 mb-4">
              <Sparkles className="h-4 w-4 text-[#80C753]" />
              <span className="text-[#80C753] text-sm font-bold uppercase tracking-wide">Elige tu plan</span>
            </div>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 tracking-tight">
              ¿Agencia o Agente? <span className="bg-gradient-to-r from-[#45A0EE] to-[#80C753] bg-clip-text text-transparent">Entiende la diferencia en 10 segundos</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Elige bien desde el inicio: no es lo mismo ser dueño del negocio que vender para alguien más.
            </p>
          </div>

          {/* Diagrama de 3 niveles */}
          <div className="max-w-5xl mx-auto mb-10 cv-fade-in-up" data-testid="niveles-diagrama">
            <div className="flex flex-col lg:flex-row items-stretch justify-center gap-3 lg:gap-2">
              {/* Nivel 1 */}
              <div className="flex-1 rounded-2xl bg-white/[0.04] border border-white/12 p-5 text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#45A0EE]/15 border border-[#45A0EE]/40 mb-2">
                  <Globe className="h-5 w-5 text-[#45A0EE]" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-wider text-[#45A0EE]">Nivel 1 · Empresa matriz</p>
                <h4 className="text-lg font-black text-white mt-0.5 mb-1">Club Viajemos</h4>
                <p className="text-xs text-slate-400">Más de 220,000 contratos directos, 330 proveedores y 3.3 millones de hoteles.</p>
              </div>
              {/* flecha */}
              <div className="flex items-center justify-center text-slate-500">
                <ArrowDown className="h-6 w-6 lg:hidden" />
                <ArrowRight className="h-6 w-6 hidden lg:block" />
              </div>
              {/* Nivel 2 */}
              <div className="flex-1 rounded-2xl bg-[#80C753]/[0.07] border border-[#80C753]/35 p-5 text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#80C753]/15 border border-[#80C753]/40 mb-2">
                  <Crown className="h-5 w-5 text-[#80C753]" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-wider text-[#80C753]">Nivel 2 · Tú eres el dueño</p>
                <h4 className="text-lg font-black text-white mt-0.5 mb-1">Agencia</h4>
                <p className="text-xs text-slate-400">Usas los contratos de Club Viajemos a tu favor y ganas el 50%, 60% u 80% de la comisión, según tu plan.</p>
              </div>
              {/* flecha */}
              <div className="flex items-center justify-center text-slate-500">
                <ArrowDown className="h-6 w-6 lg:hidden" />
                <ArrowRight className="h-6 w-6 hidden lg:block" />
              </div>
              {/* Nivel 3 */}
              <div className="flex-1 rounded-2xl bg-white/[0.04] border border-white/12 p-5 text-center">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#2DD4BF]/15 border border-[#2DD4BF]/40 mb-2">
                  <Briefcase className="h-5 w-5 text-[#2DD4BF]" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-wider text-[#2DD4BF]">Nivel 3 · Vendes para una agencia</p>
                <h4 className="text-lg font-black text-white mt-0.5 mb-1">Agente</h4>
                <p className="text-xs text-slate-400">Usas los precios que te da tu agencia y ganas hasta el 25% de comisión.</p>
              </div>
            </div>
          </div>

          {/* Dos tarjetas: Agencia vs Agente */}
          <div className="max-w-4xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-2 gap-5 cv-fade-in-up" data-testid="agencia-vs-agente">
            {/* Tarjeta Agencia */}
            <div className="rounded-2xl bg-gradient-to-br from-[#80C753]/[0.08] to-[#45A0EE]/[0.06] border-2 border-[#80C753]/40 p-6" data-testid="card-explica-agencia">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#80C753] text-[#07131f]">
                  <Crown className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black text-white leading-tight">AGENCIA = Eres el dueño del negocio</h3>
              </div>
              <ul className="space-y-2.5 mb-4">
                {[
                  'Acceso directo a los contratos y precios negociados de Club Viajemos',
                  'Ganas 50% (Starter), 60% (Profesional) u 80% (Travel Empire)',
                  'Puedes registrar tus propios agentes debajo de ti',
                  'Ganas override por las ventas de tu equipo',
                  'Construyes un negocio que crece contigo',
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-200">
                    <Check className="h-5 w-5 text-[#80C753] flex-shrink-0 mt-0.5" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#80C753] font-bold border-t border-white/10 pt-3">
                Ideal para: <span className="text-white font-semibold">"Quiero mi propia agencia y armar mi equipo."</span>
              </p>
            </div>

            {/* Tarjeta Agente */}
            <div className="rounded-2xl bg-white/[0.04] border border-white/15 p-6" data-testid="card-explica-agente">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/10 border border-white/20 text-slate-200">
                  <Briefcase className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black text-white leading-tight">AGENTE = Vendes para una agencia</h3>
              </div>
              <ul className="space-y-2.5 mb-4">
                <li className="flex items-start gap-2.5 text-sm text-slate-200"><Check className="h-5 w-5 text-[#2DD4BF] flex-shrink-0 mt-0.5" /><span>Trabajas con los contratos y el precio que te da tu agencia</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-200"><Check className="h-5 w-5 text-[#2DD4BF] flex-shrink-0 mt-0.5" /><span>Ganas hasta el 25% de comisión por tus ventas</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-400"><X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" /><span>No puedes registrar agentes debajo de ti</span></li>
                <li className="flex items-start gap-2.5 text-sm text-slate-400"><X className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" /><span>No tienes override ni precio negociado propio</span></li>
              </ul>
              <p className="text-sm text-slate-300 font-bold border-t border-white/10 pt-3">
                Ideal para: <span className="text-white font-semibold">"Solo quiero vender viajes y ganar comisiones."</span>
              </p>
            </div>
          </div>

          {/* Línea guía */}
          <div className="max-w-3xl mx-auto mb-8 text-center cv-fade-in-up">
            <p className="text-base sm:text-lg font-bold text-white leading-relaxed">
              ¿Quieres construir tu propio equipo? <span className="text-[#80C753]">→ Elige AGENCIA.</span><br className="hidden sm:block" />
              ¿Solo quieres vender viajes? <span className="text-[#2DD4BF]">→ Elige AGENTE.</span>
            </p>
          </div>

          {/* Toggle Agencias / Agente */}
          <div className="flex justify-center mb-3" data-testid="audience-toggle">
            <div className="inline-flex items-center gap-1 rounded-full bg-white/[0.06] border border-white/15 p-1.5 backdrop-blur-md">
              <button
                type="button"
                data-testid="audience-toggle-agencias"
                onClick={() => setAudience('agencias')}
                className={`px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-wide transition-all ${audience === 'agencias' ? 'bg-gradient-to-r from-[#45A0EE] to-[#80C753] text-[#07131f] shadow-lg' : 'text-slate-300 hover:text-white'}`}
              >
                Soy Agencia · Dueño
              </button>
              <button
                type="button"
                data-testid="audience-toggle-agente"
                onClick={() => setAudience('agente')}
                className={`px-6 py-2.5 rounded-full text-sm font-black uppercase tracking-wide transition-all ${audience === 'agente' ? 'bg-gradient-to-r from-[#45A0EE] to-[#2DD4BF] text-[#07131f] shadow-lg' : 'text-slate-300 hover:text-white'}`}
              >
                Soy Agente · Vendedor
              </button>
            </div>
          </div>

          {/* Línea descriptiva según selección */}
          <div className="max-w-3xl mx-auto mb-8 text-center" data-testid="audience-desc">
            <p className="text-sm text-slate-300">
              {audience === 'agencias'
                ? 'Eres dueño: usas los contratos de Club Viajemos y ganas 50% a 80%.'
                : 'Vendes para una agencia y ganas hasta 25%.'}
            </p>
          </div>

          {audience === 'agente' && (
            <div className="max-w-3xl mx-auto mb-6 text-center">
              <p className="text-sm text-slate-300">
                Los planes de <span className="text-[#2DD4BF] font-bold">Agente</span> son para personas <span className="text-white font-semibold">patrocinadas por una agencia</span> de Club Viajemos. La agencia les da acceso a la plataforma para empezar a vender o para su uso personal.
              </p>
            </div>
          )}

          {/* Main Plans - Agencias */}
          <div className={`${audience === 'agencias' ? 'grid' : 'hidden'} grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6 mb-8 items-stretch max-w-5xl mx-auto`}>
            <div className="cv-fade-in-up cv-delay-100">
            <PricingOption
              plan="starter"
              name="Starter 50"
              tipo="Agencia"
              description="Ideal para comenzar"
              payLinks={[
                { provider: "Stripe", url: "https://buy.stripe.com/aFaaEWcmY8GneuRdowds723" },
                { provider: "Hotmart", url: "https://pay.hotmart.com/T103706796G?off=smanj594&checkoutMode=10" },
                { provider: "Niubiz", url: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6866673" },
              ]}
              price={697}
              monthlyFrom={35}
              secretos={1}
              features={[
                { label: "Plataforma Agencia de Viajes", sub: ["Apertura de Agentes de Venta"] },
                { label: "Plataforma Certificados de Viaje" },
                { label: "Plataforma Asesorías Migratorias" },
                { label: "Motor de Ventas Viajemos (CRM)", sub: ["El corazón de tu agencia: convierte todas tus herramientas en ventas, en piloto automático", "Atrae clientes y los atiende al instante, de día y de noche", "Seguimiento automático por WhatsApp, email y recordatorios", "Activa tus certificados, tu plataforma y tus asesorías para vender más"] },
                { label: "Sistema White Label", sub: ["Coloca tu propio logo y marca"] },
                "Capacitación incluida",
                "Soporte por email",
                "Actualizaciones de por vida",
                "Override de reservas hasta 25%"
              ]}
              paymentPlanText={PROMO_CONFIG.starter.paymentPlanText}
              onSelect={handleSelectPlan}
            />
            </div>

            <div className="cv-fade-in-up cv-delay-300">
            <PricingOption
              plan="pro"
              name="Profesional 60"
              tipo="Agencia"
              description="El mas elegido"
              payLinks={[
                { provider: "Stripe", url: "https://buy.stripe.com/cNi8wOaeQ1dV9axbgods81I" },
                { provider: "Hotmart", url: "https://pay.hotmart.com/T103706796G?off=4qu4y4c5&checkoutMode=10&bid=1793386251215" },
                { provider: "Niubiz", url: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6952886" },
              ]}
              price={1497}
              originalPrice={2788}
              monthlyFrom={89}
              secretos={4}
              features={[
                { label: "Plataforma Agencia de Viajes", sub: ["Apertura de Agentes de Venta"] },
                { label: "Plataforma Certificados de Viaje" },
                { label: "Plataforma Asesorías Migratorias" },
                { label: "Motor de Ventas Viajemos (CRM)", sub: ["El corazón de tu agencia: convierte todas tus herramientas en ventas, en piloto automático", "Atrae clientes y los atiende al instante, de día y de noche", "Seguimiento automático por WhatsApp, email y recordatorios", "Activa tus certificados, tu plataforma y tus asesorías para vender más"] },
                { label: "Sistema White Label", sub: ["Coloca tu propio logo y marca"] },
                "Capacitación premium",
                "Soporte prioritario",
                "Comunidad exclusiva",
                "Override de reservas hasta 35%"
              ]}
              isPopular={true}
              promoPrice={promoMode ? 997 : undefined}
              promoBadge={promoMode ? '−$500 SOLO HOY' : undefined}
              promoExtra={promoMode ? 'GRATIS: 1 año de membresía Constructor de Red incluido' : undefined}
              paymentPlanText={PROMO_CONFIG.pro.paymentPlanText}
              onSelect={handleSelectPlan}
            />
            </div>

            <div className="cv-fade-in-up cv-delay-300">
            <PricingOption
              plan="empire"
              name="Travel Empire 80"
              tipo="Agencia"
              description="El nivel máximo"
              payLinks={[
                { provider: "Stripe", url: "https://buy.stripe.com/eVqfZgfzaaOvaeB0BKds80b" },
                { provider: "Hotmart", url: "https://pay.hotmart.com/T103706796G?off=uo2qngvz&checkoutMode=10" },
                { provider: "Niubiz", url: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6888742" },
              ]}
              price={4995}
              monthlyFrom={249}
              secretos={5}
              features={[
                { label: "Plataforma Agencia de Viajes", sub: ["Apertura de Agentes de Venta: GRATIS E ILIMITADAS"] },
                { label: "Plataforma Certificados de Viaje" },
                { label: "Plataforma Asesorías Migratorias" },
                { label: "Plataforma Club Vacacional (Demo)" },
                { label: "Plataforma de seguros de viaje Assist Card" },
                { label: "Motor de Ventas Viajemos (CRM)", sub: ["El corazón de tu agencia: convierte todas tus herramientas en ventas, en piloto automático", "Atrae clientes y los atiende al instante, de día y de noche", "Seguimiento automático por WhatsApp, email y recordatorios", "Activa tus certificados, tu plataforma y tus asesorías para vender más"] },
                { label: "Sistema White Label", sub: ["Coloca tu propio logo y marca"] },
                "Capacitación Directa con Carlos",
                "Carlos en una sola llamada · Atención inmediata",
                "Soporte Directo",
                "Comunidad Súper exclusiva",
                "Override de reservas hasta 55%"
              ]}
              isRecommended={true}
              promoPrice={promoMode ? 2495 : undefined}
              promoBadge={promoMode ? '−$2,500 SOLO HOY' : undefined}
              promoExtra={promoMode ? 'Solo queda 1 plan a este precio' : undefined}
              paymentPlanText={PROMO_CONFIG.empire.paymentPlanText}
              onSelect={handleSelectPlan}
            />
            </div>
          </div>

          {/* Planes Agente */}
          <div className={`${audience === 'agente' ? 'grid' : 'hidden'} grid-cols-1 gap-6 mb-8 items-stretch max-w-md mx-auto`}>
            <div className="cv-fade-in-up cv-delay-100">
            <PricingOption
              plan="cero"
              name="Agente 25"
              tipo="Agente"
              description="Empieza trabajando para una agencia"
              payLinks={[
                { provider: "Stripe", url: "https://buy.stripe.com/5kQ28qdr209RgCZ3NWds81u" },
                { provider: "Hotmart", url: "https://pay.hotmart.com/T103706796G?off=xq88tkgo&checkoutMode=10&bid=1791323394463" },
                { provider: "Niubiz", url: "https://pagolink.niubiz.com.pe/pagoseguro/CLUBVIAJEMOSPERUSAC/6866734" },
              ]}
              price={197}
              monthlyFrom={19.99}
              features={[
                { label: "Plataforma Agencia de Viajes", sub: ["Vendes y ganas tu comisión en cada reserva"] },
                { label: "Motor de Ventas Viajemos (CRM)", sub: ["El corazón de tu agencia: convierte todas tus herramientas en ventas, en piloto automático", "Atrae clientes y los atiende al instante, de día y de noche", "Seguimiento automático por WhatsApp, email y recordatorios", "Activa tus certificados, tu plataforma y tus asesorías para vender más"] },
                "25% de comisión por cada venta",
                "Capacitación y soporte incluidos",
              ]}
              agentNote="Este plan es la plataforma de la agencia que te patrocina: ella te abre el acceso para que empieces a trabajar como agente de viajes y ganes tu comisión en cada reserva, sin costos de operación propios."
            />
            </div>
          </div>

          {/* Aviso: pago único de por vida (nota al pie) */}
          <div data-testid="pago-unico-aviso" className="max-w-3xl mx-auto mb-10 px-4">
            <p className="text-[12px] sm:text-[13px] text-slate-400 leading-relaxed text-center">
              <span className="text-[#80C753] font-bold">* Un solo pago de por vida</span>, seas Agente o Agencia. Sin cargos adicionales de educación, soporte o acompañamiento. Sin mensualidades, anualidades ni reactivaciones.
            </p>
          </div>

          {/* CUADRO COMPARATIVO — 4 niveles */}
          <div data-testid="precios-comparativa" className={`${audience === 'agencias' ? 'block' : 'hidden'} cv-fade-in-up mb-12`}>
            <div className="text-center mb-6">
              <h3 className="text-2xl sm:text-3xl font-black text-white">Resumen comparativo de los 3 Planes</h3>
              <p className="text-slate-400 text-sm mt-2">Compara de un vistazo qué recibe cada agente según su nivel de arranque.</p>
            </div>

            <div className="cv-glass rounded-2xl overflow-x-auto" style={{ backgroundColor: '#0d1526' }}>
              <table className="w-full min-w-[720px] text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-[11px] uppercase tracking-wider text-slate-400 font-bold sticky left-0 bg-[#0d1526]/90 backdrop-blur">&nbsp;</th>
                    <th className="p-4 text-center text-sm font-black text-white" style={{ background: '#1d4e7e' }}>Starter</th>
                    <th className="p-4 text-center text-sm font-black text-white" style={{ background: '#4a3d8a' }}>Profesional</th>
                    <th className="p-4 text-center text-sm font-black text-[#1A1A2E] border-t-2 border-x-2 border-[#E3C766]" style={{ background: '#E3C766' }}>
                      <span className="inline-flex flex-col items-center gap-0.5">
                        Travel Empire
                        <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#E3C766] bg-[#1A1A2E] rounded-full px-2 py-0.5">★ El mejor</span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {(() => {
                    const colStyles = [
                      { background: '#12335a', color: '#eaf3ff' },
                      { background: '#332a63', color: '#efeaff' },
                      { background: '#5a4a16', color: '#F7EFCB' },
                    ];
                    return [
                    { label: "Un solo pago", vals: ["$697", "$1,497", "$4,995"], strong: true },
                    { label: "Plataforma Agencia de Viajes", vals: ["✓", "✓", "✓"] },
                    { label: "Sistema White Label (tu logo)", vals: ["✓", "✓", "✓"] },
                    { label: "Plataforma Certificados de Viaje", vals: ["✓", "✓", "✓"] },
                    { label: "Plataforma Asesorías Migratorias", vals: ["✓", "✓", "✓"] },
                    { label: "Plataforma Club Vacacional", vals: ["—", "—", "✓"] },
                    { label: "Seguros de viaje Assist Card", vals: ["—", "—", "✓"] },
                    { label: "Certificados Orlando + Miami / mes", vals: ["✓", "✓", "✓"] },
                    { label: "Apertura de Agentes de Venta", vals: ["✓", "✓", "✓"] },
                    { label: "Comisión agencia", vals: ["50/50", "60/40", "80/20"] },
                    { label: "Comisión certificados de viaje", vals: ["50/50", "60/40", "80/20"] },
                    { label: "Comisión asesorías migratorias", vals: ["50/50", "60/40", "80/20"] },
                    { label: "Comisión Club Vacacional", vals: ["—", "—", "80/20"] },
                    { label: "Bono de Registración (comisión)", vals: ["50/50", "60/40", "80/20"] },
                    { label: "Soporte", vals: ["Email", "Prioritario", "Directo con Carlos"] },
                    { label: "Extras únicos", vals: ["—", "—", "Atención directa de Carlos"] },
                  ].map((row) => (
                    <tr key={row.label} className="border-b border-white/5 last:border-0">
                      <td className="p-4 text-slate-300 font-semibold sticky left-0 bg-[#0d1526]/90 backdrop-blur whitespace-nowrap">{row.label}</td>
                      {row.vals.map((v, i) => (
                        <td
                          key={i}
                          style={colStyles[i]}
                          className={`p-4 text-center ${i === 2 ? 'font-bold border-x-2 border-[#E3C766]' : ''} ${row.strong ? 'font-black' : ''}`}
                        >
                          {v}
                        </td>
                      ))}
                    </tr>
                  ));
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* HERRAMIENTAS — Constructor de Red (solo vista Agencias) */}
          <div className={audience === 'agencias' ? 'block' : 'hidden'}>
            <NetworkBuilderSection promoMode={promoMode} />
          </div>

          {/* CUADRO COMPARATIVO — Agente */}
          <div data-testid="precios-comparativa-agente" className={`${audience === 'agente' ? 'block' : 'hidden'} cv-fade-in-up mb-12`}>
            <div className="text-center mb-6">
              <h3 className="text-2xl sm:text-3xl font-black text-white">El Plan Agente</h3>
              <p className="text-slate-400 text-sm mt-2">Accede a la plataforma patrocinado por tu agencia de Club Viajemos.</p>
            </div>

            <div className="cv-glass rounded-2xl overflow-x-auto mx-auto max-w-md" style={{ backgroundColor: '#0d1526' }}>
              <table className="w-full min-w-[320px] text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="p-4 text-[11px] uppercase tracking-wider text-slate-400 font-bold sticky left-0 bg-[#0d1526]/90 backdrop-blur">&nbsp;</th>
                    <th className="p-4 text-center text-sm font-black text-white" style={{ background: '#12335a' }}>
                      <span className="inline-flex flex-col items-center gap-0.5">
                        Agente 25
                        <span className="text-[9px] font-black uppercase tracking-[0.15em] text-[#45A0EE] bg-[#0d1526] rounded-full px-2 py-0.5">Agente</span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {(() => {
                    const colStyles = [
                      { background: '#12335a', color: '#eaf3ff' },
                    ];
                    return [
                      { label: "Costo de entrada", vals: ["$197"], strong: true },
                      { label: "Comisión en reservas de viajes", vals: ["25%"], strong: true },
                      { label: "Plataforma Agencia de Viajes", vals: ["✓"] },
                      { label: "Comprar vacaciones propias o de terceros", vals: ["✓"] },
                      { label: "Comisión en Certificados de Viaje", vals: ["0%"] },
                      { label: "Comisión en Asesorías Migratorias", vals: ["0%"] },
                      { label: "Comisión en Club Vacacional", vals: ["0%"] },
                      { label: "Bono de Registración", vals: ["—"] },
                      { label: "Soporte", vals: ["Con tu agencia"] },
                    ].map((row) => (
                      <tr key={row.label} className="border-b border-white/5 last:border-0">
                        <td className="p-4 text-slate-300 font-semibold sticky left-0 bg-[#0d1526]/90 backdrop-blur whitespace-nowrap">{row.label}</td>
                        {row.vals.map((v, i) => (
                          <td key={i} style={colStyles[i]} className={`p-4 text-center ${row.strong ? 'font-black' : ''}`}>{v}</td>
                        ))}
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-slate-400 text-center mt-3 max-w-2xl mx-auto">Los planes de Agente son otorgados y soportados por la agencia de Club Viajemos que te patrocina.</p>
          </div>


          {/* GARANTÍA */}
          <div data-testid="precios-garantia" className="cv-fade-in-up mb-12">
            <div className="relative overflow-hidden rounded-3xl border-2 border-[#80C753]/50 bg-gradient-to-br from-[#0e2a1a] via-[#0d1526] to-[#0e2a1a] p-8 sm:p-10 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(600px_300px_at_50%_-20%,rgba(128,199,83,.18),transparent_60%)] pointer-events-none" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 bg-[#80C753]/15 border border-[#80C753]/40 rounded-full px-4 py-2 mb-5">
                  <Shield className="h-4 w-4 text-[#80C753]" />
                  <span className="text-[#80C753] text-xs font-black uppercase tracking-[0.2em]">Nuestra Garantía</span>
                </div>
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight max-w-3xl mx-auto">
                  No vamos a cobrarte <span className="text-[#80C753]">nunca más</span> por las plataformas,
                  ni cuotas mensuales, ni fees por educación.
                </h3>
                <p className="text-lg sm:text-xl font-bold text-[#E3C766] mt-4">
                  Somos socios en este camino de la vida a partir de hoy.
                </p>
                <p className="text-base sm:text-lg font-semibold text-slate-200 mt-3 max-w-2xl mx-auto">
                  Solo agregas más clientes —que son tu gasolina— y sigues creciendo. 🚀
                </p>
              </div>
            </div>
          </div>

          {/* Financiamiento / socios de pago */}
          <PaymentPartners />

          {/* EMPIRE - Full width premium card */}
          <div className="relative mb-8 cv-fade-in-up" data-testid="empire-premium-card">
            <div className="absolute -inset-[2px] cv-glow-border rounded-[1.6rem] blur-[2px] opacity-80" />
            <div className="absolute -inset-6 bg-amber-400/15 rounded-[2rem] blur-[60px] pointer-events-none" />

            <div className="relative bg-white dark:bg-[#151B2B] rounded-3xl overflow-hidden">
              {/* Hero visual — sensación de libertad */}
              <div className="relative h-44 sm:h-52 md:h-60 overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/1518500/pexels-photo-1518500.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
                  alt="Destino de lujo — libertad de viajar"
                  className="w-full h-full object-cover scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#10131f] via-[#10131f]/40 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#10131f]/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
                  <div className="inline-flex items-center gap-2 bg-amber-400/20 backdrop-blur border border-amber-300/40 rounded-full px-3 py-1 mb-2">
                    <Crown className="h-3.5 w-3.5 text-amber-300" />
                    <span className="text-amber-200 text-[10px] font-black uppercase tracking-[2px]">Nivel máximo · Plan Premium</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-[0.95] drop-shadow-lg">
                    MÉTODO <span className="bg-clip-text text-transparent cv-animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg,#D4AF37,#FDE047,#D4AF37)' }}>GARANTIZADO DE ÉXITO</span>
                  </h3>
                  <p className="text-slate-200 text-sm sm:text-base mt-1.5 max-w-xl drop-shadow">
                    Creamos un sistema tipo fórmula 360: te conectamos una máquina de oportunidades, tú decides cuántas y le das click.
                  </p>
                </div>
              </div>

              <div className="p-5 md:p-6">
                {/* Descripción de la franquicia */}
                <div data-testid="empire-franquicia-desc" className="flex items-start gap-3 bg-gradient-to-r from-amber-50 to-amber-100/40 dark:from-amber-500/10 dark:to-amber-500/5 border border-amber-300/60 dark:border-amber-500/25 rounded-xl px-4 py-4 mb-5">
                  <Crown className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                    Accede a <strong className="text-[#1D1D1B] dark:text-white">todos los productos de la industria de las vacaciones</strong> que ninguna agencia promedio tiene, y además a los <strong className="text-[#1D1D1B] dark:text-white">canales más profesionales de generación de clientes</strong> — todo a un solo clic. Es como tener una agencia en el <strong className="text-[#1D1D1B] dark:text-white">centro comercial más visitado de tu país</strong>, pero en un clic.
                  </p>
                </div>


                {/* DIFERENCIADOR ESTRELLA: Followers / Seguidores (appointment setters) */}
                <div className="relative mb-3 rounded-2xl overflow-hidden border-2 border-[#45A0EE]/50 bg-gradient-to-br from-[#0B1220] via-[#111a2e] to-[#1D1D1B] p-5 sm:p-6" data-testid="precios-empire-setters">
                  <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#45A0EE]/15 blur-3xl pointer-events-none" />
                  <span className="absolute top-3 right-3 bg-[#45A0EE] text-[#0B1220] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">Equipo humano · Único en el turismo</span>
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-[#45A0EE]/20 border border-[#45A0EE]/40 flex items-center justify-center">
                      <Users className="h-6 w-6 text-[#45A0EE]" />
                    </div>
                    <div>
                      <h4 className="text-base sm:text-xl font-black text-white uppercase tracking-wide leading-tight">Followers <span className="text-[#45A0EE]">(Seguidores)</span></h4>
                      <p className="text-[11px] text-slate-400 font-semibold">Tu equipo humano que te lleva de la mano</p>
                    </div>
                  </div>
                  <p className="text-sm text-slate-300 mb-5 max-w-3xl leading-relaxed">
                    Sumamos un <span className="font-bold text-white">equipo de personas reales (appointment setters)</span> que te dan la mano en tus primeros pasos: <span className="font-bold text-white">llaman a tus prospectos, los califican y te agendan las citas</span> para que mejores la <span className="font-black text-[#80C753]">conversión de tus ventas</span>. Tú aprendes a cerrar bien mientras ellos hacen el trabajo pesado.
                  </p>

                  {/* Flujo visual de 4 pasos con flechas */}
                  <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-5">
                    {[
                      { Icon: Megaphone, t: "Entran tus leads", s: "Anuncios, contenido, referidos y webinars." },
                      { Icon: Users, t: "Tus Followers los llaman", s: "Setters humanos califican y dan seguimiento por ti." },
                      { Icon: CalendarCheck, t: "Te agendan la cita", s: "Directo en tu calendario, listas para vender." },
                      { Icon: Target, t: "Tú cierras mejor", s: "Te sientas a vender y subes tu conversión." },
                    ].map(({ Icon, t, s }, i, arr) => (
                      <div key={t} className="flex-1 flex items-center gap-2">
                        <div className="flex-1 h-full bg-white/5 border border-white/10 rounded-xl p-3.5 text-center">
                          <div className="mx-auto w-9 h-9 rounded-full bg-[#45A0EE]/15 border border-[#45A0EE]/40 flex items-center justify-center mb-2">
                            <Icon className="h-5 w-5 text-[#45A0EE]" />
                          </div>
                          <p className="text-[11px] font-black text-[#45A0EE] mb-0.5">PASO {i + 1}</p>
                          <p className="text-xs font-bold text-white leading-tight mb-1">{t}</p>
                          <p className="text-[10px] text-slate-400 leading-snug">{s}</p>
                        </div>
                        {i < arr.length - 1 && (
                          <>
                            <ArrowRight className="hidden sm:block h-5 w-5 text-[#45A0EE]/60 flex-shrink-0" />
                            <ArrowRight className="sm:hidden h-5 w-5 text-[#45A0EE]/60 flex-shrink-0 rotate-90 mx-auto" />
                          </>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <div className="bg-white/5 border border-white/10 rounded-xl p-3.5">
                      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Starter · Profesional</p>
                      <p className="text-sm text-slate-300"><span className="font-bold text-white">Tú generas y agendas</span> tus propios leads.</p>
                    </div>
                    <div className="bg-[#45A0EE]/10 border border-[#45A0EE]/40 rounded-xl p-3.5">
                      <p className="text-[10px] uppercase tracking-wider text-[#45A0EE] font-bold mb-1">Travel Empire</p>
                      <p className="text-sm text-white"><span className="font-black text-[#80C753]">Tus Followers te agendan las citas, tú solo cierras.</span></p>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-3">Citas calificadas según disponibilidad y capacidad del equipo. El volumen puede variar.</p>
                </div>

                {/* NUEVO: Sistema de Agentes de Venta + Override (multiplicador) */}
                <div className="relative mb-3 rounded-xl overflow-hidden border-2 border-[#45A0EE]/50 bg-gradient-to-br from-[#0A1A2E] via-[#0D2440] to-[#0A1A2E] p-5" data-testid="precios-empire-agentes-override">
                  <span className="absolute top-3 right-3 bg-[#45A0EE] text-[#0A1A2E] text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">Multiplicador</span>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-[#45A0EE]/20 border border-[#45A0EE]/40 flex items-center justify-center">
                      <Network className="h-5 w-5 text-[#45A0EE]" />
                    </div>
                    <h4 className="text-base sm:text-lg font-black text-white uppercase tracking-wide">Agrega vendedores y gana con su trabajo</h4>
                  </div>
                  <p className="text-sm text-blue-100/80 mb-5 max-w-3xl">
                    Debajo de tu agencia puedes abrir <span className="font-bold text-white">Agentes de Venta ilimitados</span>. Ellos venden, tú ganas un <span className="font-black text-[#45A0EE]">override</span> por cada reserva que hacen — es dinero que entra <span className="font-bold text-white">del trabajo de otras personas</span>. Mientras más agentes colocas, <span className="font-black text-[#45A0EE]">más dinero recibes sin vender tú</span>.
                  </p>

                  {/* Diagrama visual del override: TÚ arriba, agentes abajo */}
                  <div className="bg-[#061222]/60 border border-[#45A0EE]/20 rounded-xl p-4 mb-5">
                    {/* Nivel TÚ */}
                    <div className="flex flex-col items-center">
                      <div className="w-full max-w-[260px] bg-gradient-to-br from-[#45A0EE] to-[#2E7BC4] rounded-xl px-4 py-3 text-center shadow-lg shadow-[#45A0EE]/20">
                        <Crown className="h-5 w-5 text-white mx-auto mb-1" />
                        <p className="text-sm font-black text-white uppercase tracking-wide">Tú · Dueño de agencia</p>
                        <p className="text-[11px] text-blue-50/90">Recibes el override de todos tus agentes</p>
                      </div>
                      {/* Flujo de override hacia arriba */}
                      <div className="flex items-center gap-1.5 my-2 text-[#80C753]">
                        <ArrowDown className="h-4 w-4 rotate-180" />
                        <span className="text-[11px] font-bold uppercase tracking-wider">Override sube hacia ti</span>
                        <ArrowDown className="h-4 w-4 rotate-180" />
                      </div>
                      {/* Agentes abajo */}
                      <div className="grid grid-cols-3 gap-2 w-full max-w-md">
                        {[
                          { n: "Agente 1", s: "vende $5,000" },
                          { n: "Agente 2", s: "vende $5,000" },
                          { n: "Agente 3", s: "vende $5,000" },
                        ].map(({ n, s }) => (
                          <div key={n} className="bg-white/5 border border-white/10 rounded-lg px-2 py-2.5 text-center">
                            <UserPlus className="h-4 w-4 text-[#45A0EE] mx-auto mb-1" />
                            <p className="text-[11px] font-bold text-white leading-tight">{n}</p>
                            <p className="text-[10px] text-blue-100/60">{s}</p>
                          </div>
                        ))}
                      </div>
                      <p className="text-[10px] text-blue-100/50 mt-2">…y así con tantos agentes como quieras. <span className="text-[#45A0EE] font-bold">Sin límite.</span></p>
                    </div>
                  </div>

                  {/* Comparativo: multiplicador */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {[
                      { Icon: Users, t: "3 agentes", s: "Override de 3 personas vendiendo por ti", amt: "≈ $450/mes" },
                      { Icon: Layers, t: "10 agentes", s: "El equipo crece, tu override también", amt: "≈ $1,500/mes" },
                      { Icon: Wallet, t: "30 agentes", s: "Tu retiro: ingresos del trabajo de otros", amt: "≈ $4,500/mes" },
                    ].map(({ Icon, t, s, amt }) => (
                      <div key={t} className="bg-white/5 border border-[#45A0EE]/20 rounded-lg p-3 text-center">
                        <Icon className="h-5 w-5 text-[#45A0EE] mx-auto mb-1.5" />
                        <p className="text-sm font-black text-white leading-tight">{t}</p>
                        <p className="text-[10px] text-blue-100/60 leading-snug mt-0.5 mb-2">{s}</p>
                        <p className="text-base font-black text-[#80C753]">{amt}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#45A0EE]/10 border border-[#45A0EE]/30 rounded-lg px-4 py-3 flex items-start gap-2.5">
                    <TrendingUp className="h-5 w-5 text-[#45A0EE] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-white"><span className="font-black text-[#45A0EE]">Este es tu retiro:</span> mientras más agentes colocas debajo de tu agencia, más override entra cada mes de las reservas que ellos hacen.</p>
                      <p className="text-[10px] text-blue-100/50 mt-1">Ejemplo ilustrativo. El override depende del plan, del volumen de ventas de tus agentes y de las condiciones vigentes.</p>
                    </div>
                  </div>
                </div>

                {/* GHL SYSTEM - Full width premium section (replaces ex AI Assistant) */}
                <div className="relative mt-5 mb-0 rounded-2xl overflow-hidden border border-amber-200 dark:border-amber-500/30 shadow-[0_8px_30px_-12px_rgba(212,150,14,0.25)]">
                  {/* Light background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-white to-amber-50/40 dark:from-amber-950/20 dark:via-slate-900/40 dark:to-amber-950/10" />
                  {/* Gold glow accent */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-amber-200/40 dark:bg-amber-500/10 blur-3xl pointer-events-none" />

                  <div className="relative">
                    {/* HEADER */}
                    <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-amber-200/60 dark:border-amber-500/20 bg-gradient-to-r from-amber-100/50 to-transparent dark:from-amber-500/10">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-md">
                        <Zap className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] sm:text-[10px] font-mono font-bold uppercase tracking-[2px] text-amber-700 dark:text-amber-400 mb-0.5">
                          Incluido en tu plan de agencia
                        </p>
                        <h4 className="text-lg sm:text-xl font-black text-[#1D1D1B] dark:text-white uppercase tracking-wide leading-tight">
                          Motor de Ventas Viajemos (CRM)
                        </h4>
                      </div>
                    </div>

                    {/* PITCH */}
                    <div className="px-5 sm:px-6 py-5 border-b border-amber-200/40 dark:border-amber-500/10">
                      <p className="text-base sm:text-lg font-bold text-[#1D1D1B] dark:text-white leading-snug mb-2">
                        El <span className="text-amber-600 dark:text-amber-400">corazón de tu agencia</span>: convierte todas tus herramientas en ventas, en piloto automático.
                      </p>
                      <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                        Un solo sistema que atrae a tus clientes, los atiende y les hace seguimiento por ti — <strong className="text-[#1D1D1B] dark:text-white">de día y de noche</strong>. Tú solo cierras y cobras.
                      </p>
                    </div>

                    {/* STATS */}
                    <div className="grid grid-cols-3 gap-px bg-amber-200/60 dark:bg-amber-500/20 border-y border-amber-200/60 dark:border-amber-500/20">
                      {[
                        { n: '24/7', l: 'Atiende sin parar' },
                        { n: '1', l: 'Todo en un lugar' },
                        { n: 'Auto', l: 'Piloto automático' },
                      ].map(s => (
                        <div key={s.l} className="bg-white dark:bg-slate-900/40 py-3 sm:py-4 px-2 text-center">
                          <p className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 leading-none mb-1">{s.n}</p>
                          <p className="text-[9px] sm:text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider leading-tight">{s.l}</p>
                        </div>
                      ))}
                    </div>

                    {/* CÓMO FUNCIONA — flujo visual */}
                    <div className="px-5 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { icon: Megaphone, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-500/15', step: 'Atrae', t: 'Atrae clientes', d: 'Trae interesados y los atiende al instante, de día y de noche.' },
                        { icon: Repeat, color: 'text-sky-600 dark:text-sky-400', bg: 'bg-sky-100 dark:bg-sky-500/15', step: 'Da seguimiento', t: 'Seguimiento automático', d: 'WhatsApp, email y recordatorios salen solos hasta que compran.' },
                        { icon: TrendingUp, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-500/15', step: 'Vende', t: 'Vende más', d: 'Activa tus certificados, tu plataforma y tus asesorías para vender más.' },
                      ].map((f) => (
                        <div key={f.t} className="relative bg-white/60 dark:bg-slate-800/40 border border-amber-100 dark:border-amber-500/10 rounded-xl p-4">
                          <div className={`w-10 h-10 rounded-lg ${f.bg} flex items-center justify-center mb-2.5`}>
                            <f.icon className={`h-5 w-5 ${f.color}`} />
                          </div>
                          <p className="text-[9px] font-black uppercase tracking-[2px] text-amber-600 dark:text-amber-400 mb-0.5">{f.step}</p>
                          <p className="text-sm font-bold text-[#1D1D1B] dark:text-white leading-snug mb-0.5">{f.t}</p>
                          <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">{f.d}</p>
                        </div>
                      ))}
                    </div>

                    {/* CASITA EN MOVIMIENTO — fábrica Travel Empire (solo /promo) */}
                    {promoMode && (
                      <div className="px-5 sm:px-6 pb-5" data-testid="crm-factory-block">
                        <div className="rounded-xl bg-white/60 dark:bg-slate-900/40 border border-amber-100 dark:border-amber-500/15 p-4">
                          <p className="text-center text-xs sm:text-sm font-bold text-[#1D1D1B] dark:text-white mb-1">
                            Así trabaja tu motor por dentro
                          </p>
                          <p className="text-center text-[11px] text-slate-500 dark:text-slate-400 mb-3">
                            Más de 30 personas creando clientes para ti cada día.
                          </p>
                          <TravelEmpireFactory />
                        </div>
                      </div>
                    )}

                    {/* PROMESA — compacto */}
                    <div className="mx-5 sm:mx-6 mb-5 rounded-xl bg-gradient-to-br from-amber-100/70 to-amber-50/40 dark:from-amber-500/15 dark:to-amber-500/5 border border-amber-300/60 dark:border-amber-500/30 p-4 flex items-start gap-3">
                      <Crown className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                        Todas tus herramientas trabajando juntas en <strong className="text-[#1D1D1B] dark:text-white">un solo motor</strong> — <strong className="text-[#1D1D1B] dark:text-white">tú solo cierras ventas y cobras comisiones.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* DIPLOMADO FGU — Cerrador Digital Profesional */}
              <div className="relative overflow-hidden border-t border-slate-200 dark:border-slate-700/40">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F1B2D] via-[#16213E] to-[#0F3460]" />
                <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-[#45A0EE]/20 blur-3xl pointer-events-none" />
                <div className="absolute -bottom-16 -right-16 w-56 h-56 rounded-full bg-amber-400/15 blur-3xl pointer-events-none" />
                <div className="relative px-5 md:px-8 py-7">
                  {/* Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <GraduationCap className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="bg-amber-400 text-[#1A1A2E] text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Nuevo · Incluido</span>
                        <span className="bg-white/10 border border-white/20 text-white text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider">Avalado por FGU</span>
                      </div>
                      <h4 className="text-lg sm:text-2xl font-black text-white leading-tight">
                        Te diplomamos y certificamos como <span className="text-amber-300">Cerrador Digital Profesional</span>
                      </h4>
                    </div>
                  </div>

                  <p className="text-slate-300 text-sm leading-relaxed mb-5 max-w-3xl">
                    Diploma oficial avalado por <strong className="text-white">Florida Global University (FGU)</strong> en cierre de ventas para el mundo del turismo. No solo generas clientes — aprendes a convertirlos en ventas como un profesional.
                  </p>

                  {/* Stats clave */}
                  <div className="grid grid-cols-3 gap-3 mb-5">
                    {[
                      { v: '90 hrs', l: '9.0 CEU certificados' },
                      { v: 'Doble', l: 'Cierre + https://app.viajemos.us' },
                      { v: '$1.5K–20K', l: 'Comisión mensual potencial' },
                    ].map((s) => (
                      <div key={s.l} className="bg-white/5 backdrop-blur border border-white/10 rounded-xl py-3 px-2 text-center">
                        <p className="text-lg sm:text-2xl font-black text-amber-300 leading-none mb-1">{s.v}</p>
                        <p className="text-[9px] sm:text-[10px] text-slate-400 uppercase tracking-wider leading-tight">{s.l}</p>
                      </div>
                    ))}
                  </div>

                  {/* Qué dominarás */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {[
                      { icon: Target, t: 'Método NEPQ', d: 'Haz que el cliente se venda a sí mismo.' },
                      { icon: Shield, t: 'Manejo de objeciones', d: 'Convierte el "lo voy a pensar" en un "sí".' },
                      { icon: Bot, t: 'Dominio de https://app.viajemos.us', d: 'Gestiona, agenda y cierra desde una sola plataforma.' },
                      { icon: Award, t: 'Diploma oficial FGU', d: 'Certificado que respalda tu nuevo oficio digital.' },
                    ].map((f) => (
                      <div key={f.t} className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-400/15 flex items-center justify-center flex-shrink-0">
                          <f.icon className="h-4 w-4 text-amber-300" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-white leading-snug">{f.t}</p>
                          <p className="text-xs text-slate-400 leading-relaxed">{f.d}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* EQUIPO GANADOR — Hotmart Black + Circulo Elite (antes de la garantía) */}
          <div data-testid="precios-equipo-ganador" className="cv-fade-in-up mt-14">
            <div className="relative overflow-hidden rounded-3xl border border-amber-300/40 dark:border-amber-400/25 bg-gradient-to-br from-[#120f06] via-[#1a1508] to-[#120f06] p-6 sm:p-10">
              {/* Glow accents */}
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-amber-400/15 blur-[90px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#80C753]/10 blur-[90px] pointer-events-none" />

              <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-center">
                {/* Izquierda: mensaje */}
                <div className="text-center lg:text-left">
                  <span className="inline-flex items-center gap-2 bg-amber-400/15 border border-amber-300/40 rounded-full px-3 py-1 mb-4">
                    <Crown className="h-3.5 w-3.5 text-amber-300" />
                    <span className="text-amber-200 text-[10px] font-black uppercase tracking-[2px]">Sé parte del equipo ganador</span>
                  </span>
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-4">
                    Tu agencia de viajes, <span className="bg-clip-text text-transparent cv-animate-shimmer" style={{ backgroundImage: 'linear-gradient(90deg,#D4AF37,#FDE047,#D4AF37)' }}>más fácil que nunca</span>
                  </h3>
                  <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-5">
                    Si quieres tu agencia de viaje, no existe algo más fácil ni más económico. Ser una <span className="font-bold text-white">agencia host con más de 220,000 contratos directos</span> y <span className="font-bold text-white">330 proveedores</span> te convierte en una <span className="font-bold text-amber-300">súper agencia</span> de un día a otro. Y más aún si conectas una <span className="font-bold text-[#80C753]">máquina de creación de clientes</span> a nivel de agencia, tú decides cuánta.
                  </p>
                  <div className="mb-5 border-l-2 border-amber-400/60 pl-4">
                    <p className="text-slate-200 text-sm sm:text-base leading-relaxed italic">
                      Y lo más invaluable: detrás de cada clic hay <span className="font-bold text-amber-300">más de 16 años</span> y el trabajo de <span className="font-bold text-white">miles de personas</span> que lo soñaron, lo construyeron y lo comprobaron. Hoy ese esfuerzo se convierte en algo <span className="font-bold text-white">simple y listo para ti</span>: una verdadera máquina dentro de la industria de los viajes. No empiezas de cero — <span className="font-bold text-[#80C753]">empiezas donde otros tardaron años en llegar</span>.
                    </p>
                  </div>
                  {/* Stats rápidos */}
                  <div className="grid grid-cols-3 gap-2 mb-5 max-w-md mx-auto lg:mx-0">
                    {[
                      { n: "220K+", l: "Contratos directos" },
                      { n: "330", l: "Proveedores" },
                      { n: "3.3M", l: "Hoteles" },
                    ].map((s) => (
                      <div key={s.l} className="bg-white/5 border border-white/10 rounded-xl py-3 px-2 text-center">
                        <p className="text-xl sm:text-2xl font-black text-amber-300 leading-none mb-1">{s.n}</p>
                        <p className="text-[9px] sm:text-[10px] text-slate-400 uppercase tracking-wider leading-tight">{s.l}</p>
                      </div>
                    ))}
                  </div>
                  {/* Beneficios Circulo Elite */}
                  <div className="grid grid-cols-2 gap-2 max-w-md mx-auto lg:mx-0">
                    {[
                      { icon: GraduationCap, label: 'Entrenamiento Privado' },
                      { icon: Users, label: 'Leads Diarios' },
                      { icon: Share2, label: 'Telefonía Incluida' },
                      { icon: TrendingUp, label: 'Red de Agentes' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                        <item.icon className="h-4 w-4 text-amber-400 flex-shrink-0" />
                        <span className="text-[11px] sm:text-xs font-semibold text-white">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Derecha: Hotmart Black card */}
                <div>
                  <div className="flex items-stretch gap-4 bg-[#0a0a0a] rounded-2xl overflow-hidden border border-amber-400/20 shadow-xl">
                    <img src="/carlos_miami.jpg" alt="Carlos Miami Fernandez - Hotmart Black Award" className="w-32 sm:w-40 object-cover object-center flex-shrink-0" />
                    <div className="flex-1 py-4 pr-4 flex flex-col justify-center">
                      <div className="flex items-center gap-2.5 mb-2">
                        <img src="/hotmart_logo.png" alt="Hotmart" className="h-7 flex-shrink-0" />
                        <span className="text-white text-lg font-black uppercase tracking-wide">Black</span>
                      </div>
                      <p className="text-amber-400 text-sm font-bold leading-snug mb-1.5">El equipo de agencias de viajes más exitoso en Hotmart</p>
                      <p className="text-white/50 text-xs mb-2">Carlos Miami Fernandez — Keep Growing</p>
                      <p className="text-white/70 text-xs leading-snug italic">"Vas a ser parte del grupo ganador más cercano al fundador"</p>
                    </div>
                  </div>
                  <div className="mt-4 bg-gradient-to-r from-amber-400 to-amber-500 rounded-2xl px-5 py-4 text-center">
                    <p className="text-[#1a1508] text-base sm:text-lg font-black leading-tight">Sé parte del equipo ganador de CLUB VIAJEMOS</p>
                    <p className="text-[#1a1508]/80 text-xs font-semibold mt-1">"Tendrás el equipo, los clientes y el soporte para triunfar desde el día 1"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Guarantee - Premium Seal */}
          <div className="mt-14 flex justify-center">
            <div className="relative max-w-2xl w-full">
              <div className="flex flex-col md:flex-row items-center gap-6 bg-gradient-to-br from-slate-50 to-white dark:from-[#151B2B] dark:to-[#1a2035] border border-slate-200 dark:border-slate-700/50 rounded-2xl p-6 md:p-8 shadow-sm">
                {/* Seal */}
                <div className="flex-shrink-0">
                  <div className="relative w-28 h-28">
                    {/* Outer ring */}
                    <div className="absolute inset-0 rounded-full border-[3px] border-[#c9a227]" />
                    {/* Inner ring */}
                    <div className="absolute inset-[6px] rounded-full border border-[#c9a227]/40" />
                    {/* Center */}
                    <div className="absolute inset-[10px] rounded-full bg-gradient-to-br from-[#c9a227]/10 to-amber-50 dark:from-[#c9a227]/15 dark:to-amber-900/10 flex flex-col items-center justify-center">
                      <Shield className="h-6 w-6 text-[#c9a227] mb-0.5" />
                      <p className="text-[#c9a227] text-2xl font-black leading-none">7</p>
                      <p className="text-[#c9a227] text-[8px] font-bold uppercase tracking-wider">Dias</p>
                    </div>
                    {/* Top badge */}
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#c9a227] text-white text-[7px] font-black uppercase tracking-widest px-3 py-0.5 rounded-full whitespace-nowrap">
                      Garantia Total
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="text-center md:text-left flex-1">
                  <h4 className="text-lg font-black text-[#1D1D1B] dark:text-white uppercase tracking-wide mb-1">
                    Garantia de Satisfaccion <span className="text-[#c9a227]">100%</span>
                  </h4>
                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-3">
                    Tienes <span className="font-bold text-[#c9a227]">7 dias completos</span> para explorar todo el sistema. Si no es lo que esperabas, te devolvemos cada centavo. Sin preguntas, sin letras chicas, sin complicaciones.
                  </p>
                  <div className="flex items-start gap-2 mb-3 bg-amber-50 dark:bg-amber-400/10 border border-amber-300/50 dark:border-amber-400/25 rounded-lg px-3 py-2">
                    <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
                      <span className="font-bold">Importante:</span> la garantía de devolución aplica solo a compras a <span className="font-bold">precio regular</span>. Las <span className="font-bold">promociones y ofertas con descuento no tienen devolución</span> — al ser precios especiales, la compra es final.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-[#80C753]" />
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Devolucion completa</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-[#80C753]" />
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Sin preguntas</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-[#80C753]" />
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">Riesgo cero para ti</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 italic mt-3">Tu unico riesgo real es no intentarlo.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Licencias Link */}
          <div className="mt-8 text-center">
            <a
              href="/licencias"
              data-testid="licencias-link"
              className="inline-flex items-center gap-2 text-slate-400 hover:text-[#c9a227] text-sm font-medium transition-colors"
            >
              <Shield className="h-4 w-4" />
              Licencias y Registros — ST39568
              <ChevronRight className="h-3 w-3" />
            </a>
          </div>

          {/* Carlos Miami Fernandez - Final Quote (manifiesto) */}
          <div className="mt-16 mb-8 max-w-5xl mx-auto px-4">
            <div
              className="relative overflow-hidden rounded-3xl border border-[#c9a227]/35 p-7 sm:p-12"
              style={{ background: 'linear-gradient(135deg,#0a1222 0%,#0e1830 55%,#0a1222 100%)' }}
              data-testid="carlos-manifesto"
            >
              {/* Glow + decorative quote mark */}
              <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-[#c9a227]/15 blur-[90px] pointer-events-none" />
              <div className="absolute top-2 left-4 text-[140px] leading-none font-serif text-[#c9a227]/15 select-none pointer-events-none">“</div>

              <div className="relative grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-start">
                {/* Left: Carlos photo + golden line */}
                <div className="hidden md:flex flex-col items-center gap-4 pt-2">
                  <img src="/carlos_miami.jpg" alt="Carlos Miami Fernandez" className="w-24 h-24 rounded-full object-cover object-top border-[3px] border-[#c9a227] shadow-lg shadow-[#c9a227]/30" />
                  <div className="w-px flex-1 bg-gradient-to-b from-[#c9a227] to-transparent min-h-[220px]" />
                </div>

                {/* Right: The quote */}
                <div className="space-y-6">
                  <div className="flex items-center gap-3 mb-1 md:hidden">
                    <img src="/carlos_miami.jpg" alt="Carlos Miami Fernandez" className="w-14 h-14 rounded-full object-cover object-top border-2 border-[#c9a227]" />
                    <div>
                      <p className="text-white font-bold text-sm">Carlos Miami Fernández</p>
                      <p className="text-[#c9a227] text-xs font-semibold">CEO &amp; Fundador</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-slate-300 text-sm sm:text-base mb-1">La mayoría viene buscando un trabajo.</p>
                    <p className="text-white text-2xl sm:text-3xl md:text-4xl font-black leading-[1.1]">Yo les doy una <span className="text-[#e8c766]">industria</span>.</p>
                  </div>

                  <div>
                    <p className="text-slate-300 text-sm sm:text-base mb-1">La mayoría viene buscando un ingreso extra.</p>
                    <p className="text-white text-2xl sm:text-3xl md:text-4xl font-black leading-[1.1]">Yo les instalo una máquina que genera dinero <span className="text-[#e8c766]">mientras duermen</span>.</p>
                  </div>

                  <div>
                    <p className="text-slate-300 text-sm sm:text-base mb-1">La mayoría quiere ser agente de viajes.</p>
                    <p className="text-white text-xl sm:text-2xl md:text-3xl font-black leading-[1.15]">Yo los convierto en dueños de un sistema de alto rendimiento que opera en más de 190 países — <span className="text-[#e8c766]">sin experiencia, sin licencia, sin capital.</span></p>
                  </div>

                  <div className="w-20 h-px bg-[#c9a227]/50 my-2" />

                  <div className="space-y-1">
                    <p className="text-slate-200 text-lg sm:text-xl font-semibold">No estoy aquí para darte un título.</p>
                    <p className="text-[#e8c766] text-lg sm:text-2xl font-bold">Estoy aquí para cambiarte el chip.</p>
                  </div>

                  <p className="text-white font-black text-2xl sm:text-3xl md:text-4xl leading-[1.15] pt-1">
                    Y una vez que ese chip está instalado…
                    <br />
                    <span className="bg-gradient-to-r from-[#e8c766] via-[#fff2c2] to-[#c9a227] bg-clip-text text-transparent">ya nada vuelve a ser igual.</span>
                  </p>

                  {/* Signature */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pt-5">
                    <div className="w-10 h-px bg-[#c9a227]" />
                    <p className="text-white font-bold text-sm sm:text-base tracking-wide">Carlos Miami Fernández</p>
                    <span className="text-[#c9a227]/50 hidden sm:inline">|</span>
                    <p className="text-[#c9a227] text-xs sm:text-sm font-semibold">CEO &amp; Fundador — Club Viajemos / Travel Empire</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEADS CTA */}
      <section className="py-12 px-4 bg-[#0A1E3F] flex flex-col items-center text-center gap-4">
        <a
          href="/promo"
          data-testid="leads-promo-hoy-btn"
          className="group inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#E23744] to-[#ff5a67] text-white font-black tracking-wide text-sm sm:text-base shadow-lg hover:scale-105 transition-transform uppercase"
        >
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>Promo de hoy</span>
          <span className="hidden sm:inline font-semibold normal-case text-white/90 capitalize">· {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
        </a>
      </section>

      {/* Registration Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) setPaymentStatus(null);
      }}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-[#151B2B] max-h-[85vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-center text-[#1D1D1B] dark:text-white">
              Completa tu Registro
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-2">
            {/* Selected Plan */}
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-2 text-center">
              <p className="text-xs text-slate-500 dark:text-slate-400">Plan seleccionado:</p>
              <p className="font-bold text-sm text-[#45A0EE]">
                {selectedPlan === 'cero' && 'Plan Cero - $97 USD'}
                {selectedPlan === 'starter' && 'Starter - $697 USD'}
                {selectedPlan === 'pro' && 'Profesional - $1,497 USD'}
                {selectedPlan === 'empire' && 'Empire - $4,995 USD'}
                {selectedPlan === 'club_vacacional' && 'Club Vacacional - $1,997 USD'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[#1D1D1B] dark:text-white">Nombre *</Label>
                <Input
                  placeholder="Tu nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  className="bg-white dark:bg-slate-800 h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#1D1D1B] dark:text-white">Apellido *</Label>
                <Input
                  placeholder="Tu apellido"
                  value={formData.apellido}
                  onChange={(e) => setFormData({...formData, apellido: e.target.value})}
                  className="bg-white dark:bg-slate-800 h-9 text-sm"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-[#1D1D1B] dark:text-white">Email *</Label>
              <Input
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="bg-white dark:bg-slate-800 h-9 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[#1D1D1B] dark:text-white">País *</Label>
                <Select
                  value={formData.pais}
                  onValueChange={(value) => setFormData({...formData, pais: value})}
                >
                  <SelectTrigger className="bg-white dark:bg-slate-800 h-9 text-sm">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-[#1D1D1B] dark:text-white">Teléfono</Label>
                <Input
                  placeholder="+1 234 567 8900"
                  value={formData.telefono}
                  onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                  className="bg-white dark:bg-slate-800 h-9 text-sm"
                />
              </div>
            </div>

            {/* Referral Question */}
            <div className="space-y-2 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
              <Label className="text-xs text-[#1D1D1B] dark:text-white font-medium">
                ¿Fuiste referido por un agente? *
              </Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  size="sm"
                  variant={formData.fue_referido === true ? "default" : "outline"}
                  onClick={() => setFormData({...formData, fue_referido: true})}
                  className={`h-8 text-xs ${formData.fue_referido === true ? "bg-[#80C753] hover:bg-[#6ab043]" : ""}`}
                >
                  Sí
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant={formData.fue_referido === false ? "default" : "outline"}
                  onClick={() => setFormData({...formData, fue_referido: false, nombre_agente_referidor: ''})}
                  className={`h-8 text-xs ${formData.fue_referido === false ? "bg-slate-600 hover:bg-slate-700" : ""}`}
                >
                  No
                </Button>
              </div>
              
              {formData.fue_referido && (
                <div className="space-y-1 mt-2">
                  <Input
                    placeholder="Nombre del agente"
                    value={formData.nombre_agente_referidor}
                    onChange={(e) => setFormData({...formData, nombre_agente_referidor: e.target.value})}
                    className="bg-white dark:bg-slate-800 h-9 text-sm"
                  />
                </div>
              )}
            </div>

            {/* Discount Code */}
            <div className="space-y-1">
              <Label className="text-xs text-[#1D1D1B] dark:text-white">Código de descuento</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="discount-code-input"
                  placeholder="Ej: TV-ABC12345"
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountApplied(0); }}
                  className="flex-1 uppercase h-9 text-sm"
                />
                <Button
                  data-testid="validate-code-btn"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleValidateCode}
                  disabled={validatingCode || !discountCode.trim()}
                  className="shrink-0 h-9 text-xs"
                >
                  {validatingCode ? '...' : 'Aplicar'}
                </Button>
              </div>
              {discountApplied > 0 && (
                <p data-testid="discount-applied-msg" className="text-xs font-medium text-[#80C753]">
                  Descuento: -${discountApplied} USD
                </p>
              )}
            </div>

            {/* Payment Type: Solo pago unico */}

            <Button
              data-testid="submit-payment-btn"
              onClick={handleSubmit}
              disabled={loading}
              className="w-full py-5 bg-[#80C753] hover:bg-[#6ab043] text-white font-bold"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  Continuar al Pago
                  <ChevronRight className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>

            <p className="text-[10px] text-center text-slate-500">
              <Shield className="h-3 w-3 inline mr-1" />
              Pago seguro procesado por Stripe
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Toast Notifications */}
      <Toaster 
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#FFFFFF',
            border: '1px solid #E2E8F0',
            color: '#1D1D1B',
          },
        }}
      />
    </div>
  );
};

export default VIPAccessPage;
